/* eslint-disable ember/no-computed-properties-in-native-classes */
import EmberObject, { computed } from '@ember/object';
import { inject } from '@ember/service';

import FilterConstants from '../../filter-constants';
import WhyNot from './why-not';

export default class extends EmberObject {
  @inject('logger') logger;

  opAmp = null;
  limits = null;
  marginalLimits = null;
  componentsInput = null;

  @computed('componentsInput') get refV() {
    var vsMinus = this.componentsInput.vsMinus;
    var vsPlus = this.componentsInput.vsPlus;
    var result = 0;
    if (vsMinus === 0) {
      result = vsPlus / 2;
    }

    return result;
  }

  @computed('opAmp') get partNumber() {
    var name = this.opAmp.partNumber;
    var result = '';
    if (!name) {
      result = 'whyNotText_blankName';
    }

    return result;
  }

  @computed('opAmp') get status() {
    var status = this.opAmp.status;
    var result = '';
    if (status === FilterConstants.ampStatus.obsolete) {
      result = 'whyNotText_status_obsolete';
    } else if (status === FilterConstants.ampStatus.lastTimeBuy) {
      result = 'whyNotText_status_lastTimeBuy';
    }

    return result;
  }

  @computed('opAmp') get feedbackMethod() {
    var feedbackMethod = this.opAmp.feedbackMethod;
    var result = '';
    if (feedbackMethod === 'Current') {
      result = 'whyNotText_error_feedback';
    }

    return result;
  }

  @computed('opAmp', 'limits') get minStableGain() {
    var minStableGain = this.opAmp.minStableGain;
    var stabilityGain = this.limits.specialGain;
    var compPin = !!this.opAmp.spiceModels && this.opAmp.spiceModels.compPin;
    var result = null;
    if (minStableGain > stabilityGain) {
      if (compPin) {
        result = WhyNot.create({
          messageId: 'whyNotText_error_minStableGain',
          value: stabilityGain,
          additionalMessage: 'whyNotText_error_compCapAmp',
        });
      } else {
        result = WhyNot.create({ messageId: 'whyNotText_error_minStableGain', value: stabilityGain });
      }
    }

    return result;
  }

  @computed('opAmp', 'limits') get gbw() {
    var gbw = this.opAmp.gbw;
    var minOpAmpGbw = this.limits.gbw;
    var result = '';
    if (minOpAmpGbw > gbw) {
      result = 'whyNotText_error_GBW';
    }

    return result;
  }

  @computed('opAmp', 'componentsInput') get supplyVoltage() {
    var vsPlus = this.componentsInput.vsPlus;
    var vsMinus = this.componentsInput.vsMinus;
    var minSupplyVoltage = this.opAmp.minSupplyVoltage;
    var maxSupplyVoltage = this.opAmp.maxSupplyVoltage;
    var supplySpan = vsPlus - vsMinus;
    var result = '';
    if (supplySpan < minSupplyVoltage) {
      result = 'whyNotText_error_supplyVoltageLow';
    } else if (supplySpan > maxSupplyVoltage) {
      result = 'whyNotText_error_supplyVoltageHigh';
    }

    return result;
  }

  @computed('opAmp') get numberChannels() {
    var packageCode = this.opAmp.packageCode;
    var numberChannels = this.opAmp.numberChannels;
    var result = '';
    if (!packageCode) {
      this.logger.error('no package code');
    } else {
      if (numberChannels > 2) {
        if (numberChannels === 4) {
          result = 'whyNotText_error_quadOpAmp';
        } else {
          result = 'whyNotText_error_channelSupport';
        }
      } else if (numberChannels === 2) {
        if (
          packageCode.indexOf('R-8') === -1 &&
          packageCode.indexOf('RD-8-1') === -1 &&
          packageCode.indexOf('RD-8-2') === -1 && //SOICs
          packageCode.indexOf('RM-8') === -1
        ) {
          result = 'whyNotText_error_packageIncompatible';
        }
      } else if (numberChannels === 1) {
        if (
          packageCode.indexOf('R-8') === -1 &&
          packageCode.indexOf('RD-8-1') === -1 &&
          packageCode.indexOf('RD-8-2') === -1 && //SOICs
          packageCode.indexOf('RJ-5') === -1 &&
          packageCode.indexOf('RJ-6') === -1
        ) {
          result = 'whyNotText_error_packageIncompatible';
        }
      }
    }

    return result;
  }

  @computed('opAmp', 'limits') get rOut() {
    var rOut = this.opAmp.rOut;
    var gbw = this.opAmp.gbw;
    var supplyCurrent = this.opAmp.supplyCurrent;
    var maxROutOverGbw = this.limits.rOutOverGBW;
    var result = '';
    if (rOut && rOut > 0) {
      if (maxROutOverGbw < rOut / gbw) {
        result = 'whyNotText_error_outputImpedenceHigh';
      }
    } else if (!rOut || rOut === 0) {
      var proxyRout = 0.3 / supplyCurrent;
      if (maxROutOverGbw < proxyRout / gbw) {
        result = 'whyNotText_error_outputImpedenceHigh';
      }
    }

    return result;
  }

  @computed('opAmp', 'limits') get slewRate() {
    var slewRate = this.opAmp.slewRate;
    var minSlew = this.limits.slewRate;
    var result = '';
    if (slewRate < minSlew) {
      result = 'whyNotText_error_slewRateLow';
    }

    return result;
  }

  @computed('opAmp', 'componentsInput', 'refV') get positiveInput() {
    var vsPlus = this.componentsInput.vsPlus;
    var inHeadroomPlus = this.opAmp.inHeadroomPlus;
    var refV = this.refV;
    var result = '';
    if (vsPlus - inHeadroomPlus - 0.1 <= refV) {
      result = 'whyNotText_error_notEnoughPositiveInput';
    }

    return result;
  }

  @computed('opAmp', 'componentsInput', 'refV') get positiveOutput() {
    var vsPlus = this.componentsInput.vsPlus;
    var outHeadroomPlus = this.opAmp.outHeadroomPlus;
    var refV = this.refV;
    var result = '';
    if (vsPlus - outHeadroomPlus - 0.1 <= refV) {
      result = 'whyNotText_error_notEnoughPositiveOutput';
    }

    return result;
  }

  @computed('opAmp', 'componentsInput', 'refV') get negativeInput() {
    var vsPlus = this.componentsInput.vsPlus;
    var inHeadroomMinus = this.opAmp.inHeadroomMinus;
    var refV = this.refV;
    var result = '';
    if (vsPlus - inHeadroomMinus - 0.1 <= refV) {
      result = 'whyNotText_error_notEnoughNegativeInput';
    }

    return result;
  }

  @computed('opAmp', 'componentsInput', 'refV') get negativeOutput() {
    var vsPlus = this.componentsInput.vsPlus;
    var outHeadroomMinus = this.opAmp.outHeadroomMinus;
    var refV = this.refV;
    var result = '';
    if (vsPlus - outHeadroomMinus - 0.1 <= refV) {
      result = 'whyNotText_error_notEnoughNegativeOutput';
    }

    return result;
  }

  @computed('opAmp', 'limits') get iBias() {
    var iBias = this.opAmp.iBias;
    var maxBias = this.limits.iBias;
    var result = '';
    if (iBias > maxBias) {
      result = 'amp_status_ibias_high';
    }

    return result;
  }

  @computed('positiveInput', 'positiveOutput', 'negativeInput', 'negativeOutput') get inputOutputValidationArray() {
    var positiveInput = this.positiveInput;
    var positiveOutput = this.positiveOutput;
    var negativeInput = this.negativeInput;
    var negativeOutput = this.negativeOutput;
    var result = [positiveInput, positiveOutput, negativeInput, negativeOutput];
    return result;
  }

  @computed(
    'opAmp',
    'name',
    'status',
    'feedbackMethod',
    'minStableGain',
    'gbw',
    'supplyVoltage',
    'numberChannels',
    'rOut',
    'slewRate',
  )
  get initialValidationArray() {
    var name = this.name;
    var status = this.status;
    var feedbackMethod = this.feedbackMethod;
    var minStableGain = this.minStableGain;
    var gbw = this.gbw;
    var supplyVoltage = this.supplyVoltage;
    var numberChannels = this.numberChannels;
    var rOut = this.rOut;
    var slewRate = this.slewRate;
    var result = [name, status, feedbackMethod, minStableGain, gbw, supplyVoltage, numberChannels, rOut, slewRate];
    return result;
  }

  @computed('initialValidationArray') get initialValidationHasErrors() {
    var initialValidationArray = this.initialValidationArray;
    var hasError =
      initialValidationArray.filter((item) => {
        return item && item !== '';
      }).length > 0;
    return hasError;
  }

  @computed('inputOutputValidationArray', 'initialValidationHasErrors', 'iBias') get whyNotTextArray() {
    var inputOutputValidationArray = this.inputOutputValidationArray;
    var initialValidationArray = this.initialValidationArray;
    var initialValidationHasErrors = this.initialValidationHasErrors;
    var iBias = this.iBias;

    var result = initialValidationArray;
    if (!initialValidationHasErrors) {
      result.push(inputOutputValidationArray);
    }

    result.push(iBias);
    result = [].concat.apply([], result);
    result = result.filter((item) => {
      return item && item !== '';
    });
    return result;
  }

  @computed('whyNotTextArray') get opAmpNotAllowed() {
    var whyNotTextArray = this.whyNotTextArray;
    var result = whyNotTextArray.length > 0;
    return result;
  }

  @computed('marginalLimits', 'opAmp') get marginalGbwLow() {
    var marginalLimitsGbw = this.marginalLimits.gbw;
    var gbw = this.opAmp.gbw;
    var result = '';
    if (marginalLimitsGbw > gbw) {
      result = 'amp_status_gbw_low';
    }

    return result;
  }

  @computed('opAmp') get marginalStatus() {
    var status = this.opAmp.status;
    var result = '';
    if (status === FilterConstants.ampStatus.prerelease) {
      result = 'amp_status_prerelease';
    } else if (status === FilterConstants.ampStatus.not_recommended_for_new_designs) {
      result = 'amp_status_not_recommended_for_new_designs';
    }

    return result;
  }

  @computed('marginalLimits', 'opAmp') get marginalROutHigh() {
    var recommendedROutOverGbw = this.marginalLimits.rOutOverGBW;
    var rOut = this.opAmp.rOut;
    var gbw = this.opAmp.gbw;
    var result = '';
    if (recommendedROutOverGbw < rOut / gbw) {
      result = 'amp_status_rout_high';
    }

    return result;
  }

  @computed('marginalLimits', 'opAmp') get marginalIBias() {
    var maxBias = this.marginalLimits.iBias;
    var iBias = this.opAmp.iBias;
    var result = '';
    if (iBias > maxBias) {
      result = 'amp_status_ibias_high';
    }

    return result;
  }

  @computed('marginalGbwLow', 'marginalStatus', 'marginalROutHigh', 'marginalIBias') get marginalWhy() {
    var marginalGbwLow = this.marginalGbwLow;
    var marginalStatus = this.marginalStatus;
    var marginalROutHigh = this.marginalROutHigh;
    var marginalIBias = this.marginalIBias;
    var result = [marginalGbwLow, marginalStatus, marginalROutHigh, marginalIBias];
    result = result.filter((item) => {
      return item && item !== '';
    });
    return result;
  }

  @computed('marginalWhy') get opAmpMarginal() {
    var marginalWhy = this.marginalWhy;
    return marginalWhy.length > 0;
  }
}
